/**
 * `AIObservabilityModel.ts` — port of Unity's `AIObservabilityModel.cs`.
 *
 * Typed, redacted telemetry trackers (message contents are never recorded —
 * lengths and metadata only, matching Unity's `Message Redacted=true`
 * discipline). The web slice keeps a rolling in-memory event buffer and
 * mirrors to `console.debug` in dev. Realtime usage also goes through the
 * existing Dynamo telemetry transport as an `analytics` source row, which is
 * the current Mixpanel ingestion path until the M13 analytics SDK bridge lands.
 */

import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ITelemetryService } from "@models/telemetry/ITelemetryService";
import { randomUuid } from "@models/utils/uuid";
import type { UsageDelta } from "./AISessionUsageTracker";

export interface ObservabilityEvent {
  readonly at: number;
  readonly name: string;
  readonly props: Readonly<Record<string, string | number | boolean>>;
}

const MAX_EVENTS = 200;
const K_REALTIME_USAGE_EVENT_TYPE = "ai_realtime_usage";

/**
 * ⚠ TOKENS ONLY — no money (the `*EstimatedUsd` pair was removed 2026-10-15).
 * A cost figure computed in the client bakes in whichever rates shipped with
 * that build, records nothing about which they were, and so cannot be
 * corrected later; the stored token counts can be re-priced at any time.
 * See `AISessionUsageTracker`'s header.
 */
export interface RealtimeUsageTelemetryTotals {
  readonly totalTokens: number;
  readonly realtimeTokens: number;
}

export interface IAIObservabilityModel {
  trackSessionStarted(sessionId: string, reason: string, modelName: string): void;
  trackSessionSummary(sessionId: string, totalTokens: number): void;
  trackRealtimeUsage(
    sessionId: string,
    delta: UsageDelta & { readonly source: string },
    totals: RealtimeUsageTelemetryTotals,
  ): void;
  trackUserMessage(sessionId: string, message: string, inputType: "text" | "voice"): void;
  noteAssistantResponseDelta(): void;
  trackAssistantMessage(sessionId: string, message: string): void;
  trackToolExecution(sessionId: string, toolName: string, argKeys: ReadonlyArray<string>, triggeredBy: string): void;
  trackError(sessionId: string, code: string): void;
  readonly recentEvents: ReadonlyArray<ObservabilityEvent>;
}

export class AIObservabilityModel implements IAIObservabilityModel {
  private readonly m_events: ObservabilityEvent[] = [];
  private readonly m_auth: IAuthModel;
  private readonly m_project: IProjectModel;
  private readonly m_telemetry: ITelemetryService;
  private m_lastUserMessageAt = 0;
  private m_firstDeltaAt = 0;

  constructor(auth: IAuthModel, project: IProjectModel, telemetry: ITelemetryService) {
    this.m_auth = auth;
    this.m_project = project;
    this.m_telemetry = telemetry;
  }

  private push(name: string, props: Record<string, string | number | boolean>): void {
    const event: ObservabilityEvent = { at: Date.now(), name, props };
    this.m_events.push(event);
    if (this.m_events.length > MAX_EVENTS) this.m_events.shift();
    if (import.meta.env.DEV) {
      console.debug(`[AIObservability] ${name}`, props);
    }
  }

  trackSessionStarted(sessionId: string, reason: string, modelName: string): void {
    this.push("AI Session Started", { sessionId, reason, modelName });
  }

  trackSessionSummary(sessionId: string, totalTokens: number): void {
    this.push("AI Session Usage", { sessionId, totalTokens });
  }

  // here is sample DDB record
  // note you can query by {accountid}:session and date range on the sk, 
  // then filter by event_type=ai_realtime_usage to get all the usage events for a given account and date range
//   {
//  "pk": "d05a47d4-9f6e-448d-b4d4-d5d888ec7a00:session",
//  "sk": "E#1788198353271#538bfa6a-99fd-43c2-a9e1-5cbe65f5d21a",
//  "event_type": "ai_realtime_usage",
//  "event_uid": "538bfa6a-99fd-43c2-a9e1-5cbe65f5d21a",
//  "props_json": "{\"sessionId\":\"web-cd6e3911\",\"usageSource\":\"Realtime\",\"model\":\"gpt-realtime-2.1\",\"totalTokens\":14324,\"realtimeTokens\":14259,\"deltaInputTokens\":14044,\"deltaOutputTokens\":187,\"deltaInputAudioTokens\":28,\"deltaOutputAudioTokens\":0,\"deltaCachedInputTokens\":0,\"projectId\":\"8c3c78cf-876b-4b9a-a7cb-7b0025ff58a4\"}",
//  "source": "analytics",
//  "ts": 1788198353271,
//  "user_id": "c1fbd580-0051-70b1-b336-6351606a9f21"
// }
//
// `model` + the five delta counts are what a cost report is derived FROM, so
// they are the fields not to drop. `deltaCachedInputTokens` is a SUBSET of
// `deltaInputTokens` — subtract, never sum. Rows written before 2026-10-15
// also carry `totalEstimatedUsd` / `realtimeEstimatedUsd`; those were computed
// in-client and are unreliable (inflated ~3x on web). Ignore them and re-price
// from the counts.

  trackRealtimeUsage(
    sessionId: string,
    delta: UsageDelta & { readonly source: string },
    totals: RealtimeUsageTelemetryTotals,
  ): void {
    if (delta.source !== "Realtime") return;

    this.push("AI Realtime Usage", {
      sessionId,
      model: delta.model,
      totalTokens: totals.totalTokens,
      deltaInputTokens: delta.inputTokens,
      deltaOutputTokens: delta.outputTokens,
      deltaInputAudioTokens: delta.inputAudioTokens ?? 0,
      deltaOutputAudioTokens: delta.outputAudioTokens ?? 0,
      deltaCachedInputTokens: delta.cachedInputTokens ?? 0,
    });

    const user = this.m_auth.currentUser;
    const customerId = user?.customerId ?? "";
    if (!user || !customerId) return;

    const props: Record<string, string | number> = {
      sessionId,
      usageSource: delta.source,
      model: delta.model,
      totalTokens: totals.totalTokens,
      realtimeTokens: totals.realtimeTokens,
      deltaInputTokens: delta.inputTokens,
      deltaOutputTokens: delta.outputTokens,
      deltaInputAudioTokens: delta.inputAudioTokens ?? 0,
      deltaOutputAudioTokens: delta.outputAudioTokens ?? 0,
      deltaCachedInputTokens: delta.cachedInputTokens ?? 0,
    };
    const projectId = this.m_project.activeProjectId;
    if (projectId) props.projectId = projectId;

    try {
      this.m_telemetry.capture({
        customerId,
        source: "analytics",
        eventType: K_REALTIME_USAGE_EVENT_TYPE,
        eventUid: randomUuid(),
        originatingUserId: user.userId,
        timestampMillis: Date.now(),
        propsJson: JSON.stringify(props),
      });
    } catch (error) {
      console.warn("[AIObservabilityModel] Failed to capture Realtime usage telemetry:", error);
    }
  }

  trackUserMessage(sessionId: string, message: string, inputType: "text" | "voice"): void {
    this.m_lastUserMessageAt = Date.now();
    this.m_firstDeltaAt = 0;
    this.push("AI User Message", {
      sessionId,
      inputType,
      messageLength: message.length,
      redacted: true,
    });
  }

  noteAssistantResponseDelta(): void {
    if (this.m_firstDeltaAt === 0) this.m_firstDeltaAt = Date.now();
  }

  trackAssistantMessage(sessionId: string, message: string): void {
    const latencyMs =
      this.m_firstDeltaAt > 0 && this.m_lastUserMessageAt > 0
        ? this.m_firstDeltaAt - this.m_lastUserMessageAt
        : -1;
    this.push("AI Assistant Message", {
      sessionId,
      messageLength: message.length,
      firstTokenLatencyMs: latencyMs,
      redacted: true,
    });
  }

  trackToolExecution(
    sessionId: string,
    toolName: string,
    argKeys: ReadonlyArray<string>,
    triggeredBy: string,
  ): void {
    this.push("AI Tool Execution", {
      sessionId,
      toolName,
      argKeys: argKeys.join(","),
      triggeredBy,
    });
  }

  trackError(sessionId: string, code: string): void {
    this.push("AI Error", { sessionId, code });
  }

  get recentEvents(): ReadonlyArray<ObservabilityEvent> {
    return this.m_events;
  }
}

export const AI_OBSERVABILITY_MODEL = Symbol.for("Campfire.AIObservabilityModel");
